/**
 * Composable for managing the PixiJS map renderer
 *
 * Connects three things: pointer input from the renderer goes to the tools,
 * tile changes from the map store go to the renderer, and view state is kept
 * in sync between the renderer and the editor store.
 */

import { ref, shallowRef, watch, onMounted, onUnmounted, Ref } from 'vue';
import { MapRenderer, CursorTile } from '@/pixi/MapRenderer';
import { useMapStore } from '@/stores/map';
import { useEditorStore } from '@/stores/editor';
import { useAssetsStore } from '@/stores/assets';
import { useTools } from '@/composables/useTools';
import { TileData } from '@/formats/types';

export function useMapRenderer(containerRef: Ref<HTMLElement | null>) {
    const mapStore = useMapStore();
    const editorStore = useEditorStore();
    const assetsStore = useAssetsStore();
    const tools = useTools();

    const renderer = shallowRef<MapRenderer | null>(null);
    const isInitialized = ref(false);

    let unsubscribeTileChanges: (() => void) | null = null;

    // Initialize renderer when container is available
    async function initRenderer(): Promise<void> {
        if (!containerRef.value || isInitialized.value) return;

        const newRenderer = new MapRenderer({
            container: containerRef.value,
            getBaseTile: (x, y) => {
                const tile = mapStore.document?.tiles[x]?.[y];
                if (!tile) return null;
                return {
                    texture: assetsStore.getTileTexture(tile.tileSprite, tile.tileSpriteFrame),
                    colorKey: assetsStore.getColorKey(tile.tileSprite, tile.tileSpriteFrame),
                };
            },
            getOverlayTile: (x, y) => mapStore.document?.tiles[x]?.[y] ?? null,
            onTileHover: handleTileHover,
            onTileRightClick: tools.pickAt,
            onDragStart: tools.beginStroke,
            onDrag: tools.continueStroke,
            onDragEnd: tools.endStroke,
            onViewChange: handleViewChange,
            onResize: handleResize,
        });

        await newRenderer.init();

        renderer.value = newRenderer;
        isInitialized.value = true;
        unsubscribeTileChanges = mapStore.onTileChanged(handleTileChanged);

        newRenderer.setShowGrid(editorStore.showGrid);
        newRenderer.setShowObjects(editorStore.showObjects);
        updateOverlayVisibility();

        // If map is already loaded, render it
        if (mapStore.document) {
            renderMap();
        }
    }

    // Show a newly opened map
    function renderMap(): void {
        if (!renderer.value || !mapStore.document) return;

        const { width, height } = mapStore.document;

        renderer.value.setMapSize(width, height);
        renderer.value.centerOn(width / 2, height / 2);
        renderAllObjects();
        refreshCursor();
    }

    // Base tiles are pulled by the renderer on demand; objects are pushed
    function renderAllObjects(): void {
        if (!renderer.value || !mapStore.document) return;

        const { width, height, tiles } = mapStore.document;

        for (let x = 0; x < width; x++) {
            for (let y = 0; y < height; y++) {
                if (tiles[x][y].objectSprite > 0) {
                    renderObjectAt(x, y, tiles[x][y]);
                }
            }
        }
    }

    function renderObjectAt(x: number, y: number, tile: TileData): void {
        if (!renderer.value) return;

        // Sprite ID 0 means no object
        if (tile.objectSprite <= 0) {
            renderer.value.setObjectSprite(x, y, null);
            return;
        }

        const texture = assetsStore.getTileTexture(tile.objectSprite, tile.objectSpriteFrame);
        const entry = assetsStore.getAtlasEntry(tile.objectSprite, tile.objectSpriteFrame);
        renderer.value.setObjectSprite(x, y, texture, entry?.pivot?.x ?? 0, entry?.pivot?.y ?? 0);
    }

    // Sprites whose spritesheet load has been requested from here
    const requestedSprites = new Set<number>();

    // Edits can introduce a sprite nothing has loaded yet (paste, a brush set
    // without the palette). The textureVersion watcher redraws when it arrives.
    function ensureSpriteLoaded(spriteId: number): void {
        if (requestedSprites.has(spriteId)) return;
        requestedSprites.add(spriteId);
        void assetsStore.loadSpritesForIds(new Set([spriteId]));
    }

    // A tile was edited, by any tool, panel or undo
    function handleTileChanged(x: number, y: number, tile: TileData): void {
        ensureSpriteLoaded(tile.tileSprite);
        if (tile.objectSprite > 0) {
            ensureSpriteLoaded(tile.objectSprite);
        }

        renderer.value?.invalidateTile(x, y);
        renderObjectAt(x, y, tile);
    }

    // ---- Cursor -----------------------------------------------------------

    function refreshCursor(): void {
        if (!renderer.value) return;

        const x = editorStore.tileX;
        const y = editorStore.tileY;

        if (x < 0 || y < 0 || editorStore.isSelecting) {
            renderer.value.updateCursor(-1, -1);
            return;
        }

        const cursorTiles: CursorTile[] = tools.getPreviewTiles(x, y).map(preview => {
            if (preview.spriteId === undefined || preview.frame === undefined) {
                return { offsetX: preview.offsetX, offsetY: preview.offsetY };
            }

            const entry = preview.isObject ? assetsStore.getAtlasEntry(preview.spriteId, preview.frame) : null;
            return {
                offsetX: preview.offsetX,
                offsetY: preview.offsetY,
                texture: assetsStore.getTileTexture(preview.spriteId, preview.frame),
                pivotX: entry?.pivot?.x ?? 0,
                pivotY: entry?.pivot?.y ?? 0,
            };
        });

        renderer.value.updateCursor(x, y, cursorTiles);
    }

    // Handle tile hover
    function handleTileHover(x: number, y: number): void {
        const inMap = x >= 0 && y >= 0 && x < mapStore.mapWidth && y < mapStore.mapHeight;
        const hoverX = inMap ? x : -1;
        const hoverY = inMap ? y : -1;

        // Only update if tile position changed
        if (hoverX !== editorStore.tileX || hoverY !== editorStore.tileY) {
            editorStore.setHoveredTile(hoverX, hoverY);
            refreshCursor();
        }
    }

    // The preview depends on the tool and brush, not just the position
    watch(
        [
            () => editorStore.currentTool,
            () => editorStore.brushSize,
            () => editorStore.alignTilePattern,
            () => editorStore.tileBrush,
            () => editorStore.objectBrush,
            () => editorStore.isSelecting,
            () => assetsStore.textureVersion,
        ],
        refreshCursor,
        { deep: true }
    );

    // ---- View -------------------------------------------------------------

    // Flag to prevent feedback loops between renderer and store
    let updatingFromRenderer = false;

    function handleViewChange(panX: number, panY: number, zoom: number): void {
        updatingFromRenderer = true;
        editorStore.panX = panX;
        editorStore.panY = panY;
        editorStore.zoom = zoom;
        updatingFromRenderer = false;
    }

    function handleResize(width: number, height: number): void {
        editorStore.viewportWidth = width;
        editorStore.viewportHeight = height;
    }

    // View changes made outside the renderer (toolbar zoom, minimap, shortcuts)
    watch(
        [() => editorStore.panX, () => editorStore.panY, () => editorStore.zoom],
        ([panX, panY, zoom]) => {
            if (!updatingFromRenderer) {
                renderer.value?.setView(panX, panY, zoom);
            }
        },
        { flush: 'sync' }
    );

    // ---- Store -> renderer ------------------------------------------------

    // The document ref is only replaced when a different map is opened
    watch(() => mapStore.document, (doc) => {
        if (isInitialized.value && doc) {
            renderMap();
        }
    });

    // Spritesheets load after the map is shown, and while painting new sprites.
    // Debounced because a map load brings in many spritesheets back to back.
    let assetRenderTimeout: ReturnType<typeof setTimeout> | null = null;
    watch(() => assetsStore.textureVersion, () => {
        if (assetRenderTimeout) {
            clearTimeout(assetRenderTimeout);
        }
        assetRenderTimeout = setTimeout(() => {
            assetRenderTimeout = null;
            renderer.value?.invalidateAllTiles();
            renderAllObjects();
        }, 100);
    });

    // Watch for view settings
    watch(() => editorStore.showGrid, (show) => {
        renderer.value?.setShowGrid(show);
    });

    watch(() => editorStore.showObjects, (show) => {
        renderer.value?.setShowObjects(show);
    });

    watch(() => editorStore.selectionBounds, (bounds) => {
        renderer.value?.setSelection(bounds);
    });

    function updateOverlayVisibility(): void {
        renderer.value?.setOverlayVisibility({
            blocked: editorStore.showBlocked,
            teleport: editorStore.showTeleports,
            farming: editorStore.showFarming,
            buildable: editorStore.showBuildable,
            water: editorStore.showWater,
        });
    }

    watch(
        [
            () => editorStore.showBlocked,
            () => editorStore.showTeleports,
            () => editorStore.showFarming,
            () => editorStore.showBuildable,
            () => editorStore.showWater,
        ],
        updateOverlayVisibility
    );

    // Space held = left-drag pans
    watch(() => editorStore.panMode, (enabled) => {
        renderer.value?.setPanMode(enabled);
    }, { flush: 'sync' });

    // Lifecycle
    onMounted(async () => {
        await initRenderer();
    });

    onUnmounted(() => {
        if (assetRenderTimeout) {
            clearTimeout(assetRenderTimeout);
            assetRenderTimeout = null;
        }
        unsubscribeTileChanges?.();
        unsubscribeTileChanges = null;
        renderer.value?.destroy();
        renderer.value = null;
    });

    return {
        renderer,
        isInitialized,
    };
}
